import React, {FC, useContext, useState} from "react";
import {
    Box,
    Button,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import {NextWindowContext} from "../../../context/NextWindowContext";

interface Answer {
    answer: string;
    explanation: string;
    is_correct: boolean;
}

interface Question {
    question: string;
    answers: Answer[];
}

interface TestComponentProps {
    data: { test_name: string; questions: Question[] };
    isFinished?: boolean;
    lastLesson?: boolean;
}

export const TestComponent: FC<TestComponentProps> = ({lastLesson, isFinished, data}) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
    const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: boolean }>({});
    const [openDialog, setOpenDialog] = useState(false);
    const [nextBtn, setNextBtn] = useState(false)
    const nextWindow = useContext(NextWindowContext)

    const handleAnswerSelect = (answer: Answer) => {
        setSelectedAnswer(answer.answer);
        setSelectedAnswers({...selectedAnswers, [currentQuestionIndex]: answer.is_correct});
    };

    const handleNextQuestion = () => {
        if (currentQuestionIndex < data.questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
            setSelectedAnswer(null);
        } else {
            setOpenDialog(true);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setNextBtn(true);
    };

    const correctAnswersCount = Object.values(selectedAnswers).filter(Boolean).length;
    const percentage = (correctAnswersCount / data.questions.length) * 100;

    const currentQuestion = data.questions[currentQuestionIndex];

    const AnswerOption: FC<{ answer: Answer; index: number }> = ({answer, index}) => {
        const isSelected = selectedAnswer === answer.answer;
        const isAnswerChosen = selectedAnswers[currentQuestionIndex] !== undefined;

        return (
            <Box key={index}>
                <FormControlLabel
                    control={<Radio disabled={isAnswerChosen}/>}
                    label={answer.answer}
                    value={answer.answer}
                />
                {isSelected && (
                    <Typography sx={{ml: 4, color: selectedAnswers[currentQuestionIndex] ? 'green' : 'red'}}>
                        {selectedAnswers[currentQuestionIndex] ? 'Ответ верный!' : (answer.explanation || 'Объяснение отсутствует.')}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{width: '100%', padding: '32px'}}>
            <Typography sx={{fontSize: '28px', fontWeight: 800, mb: 2}}>{currentQuestion.question}</Typography>
            <RadioGroup
                value={selectedAnswer}
                onChange={(event, value) => {
                    const answer = currentQuestion.answers.find(ans => ans.answer === value);
                    if (answer && selectedAnswer === null) {
                        handleAnswerSelect(answer);
                    }
                }}
            >
                {currentQuestion.answers.map((answer, index) => (
                    <AnswerOption answer={answer} index={index}/>
                ))}
            </RadioGroup>
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                {!nextBtn ? <Button
                    variant="contained"
                    onClick={handleNextQuestion}
                    disabled={selectedAnswer === null}
                  >
                      {currentQuestionIndex < data.questions.length - 1 ? "Следующий вопрос" : "Завершить тест"}
                  </Button> : null}
                {nextBtn ? <Button
                    className='close-modal-btn'
                    sx={{marginLeft: '15px'}}
                    variant='contained'
                    onClick={() => {
                        nextWindow()
                    }}
                >
                    {isFinished && lastLesson ? 'Завершить прохождение курса' : 'Далее'}
                </Button> : null}

            </Box>
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Результаты теста</DialogTitle>
                <DialogContent>
                    <Typography>Правильных ответов: {correctAnswersCount} из {data.questions.length}</Typography>
                    <Typography>Процент правильных ответов: {percentage.toFixed(2)}%</Typography>
                    <List>
                        {data.questions.map((question, index) => {
                            const isCorrect = selectedAnswers[index];
                            return !isCorrect ? (
                                <ListItem key={index}>
                                    <ListItemText
                                        primary={`Вопрос: ${question.question}`}
                                        secondary={
                                            question.answers.find(ans => ans.is_correct)?.explanation !== undefined && question.answers.find(ans => ans.is_correct)?.explanation !== null
                                                ? `Объяснение: ${question.answers.find(ans => ans.is_correct)?.explanation}`
                                                : "Объяснение отсутствует."
                                        }
                                    />
                                </ListItem>
                            ) : null;
                        })}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
